(() => {
  if (customElements.get('kekilli-atelier')) return;
  class KekilliAtelier extends HTMLElement {
    connectedCallback() {
      if (this.abort && !this.abort.signal.aborted) return;
      this.abort = new AbortController();
      this.hero = this.querySelector('.atelier-hero');
      this.stage = this.querySelector('.atelier-stage');
      this.film = this.querySelector('[data-film]');
      this.filmStage = this.querySelector('.atelier-film-stage');
      this.video = this.querySelector('.atelier-film-video');
      this.filmButton = this.querySelector('[data-film-toggle]');
      this.filmStatus = this.querySelector('[data-film-status]');
      this.filmLabels = JSON.parse(this.querySelector('[data-film-labels]')?.textContent || '{}');
      this.editorial = this.querySelector('.atelier-editorial-image');
      this.button = this.querySelector('[data-motion-toggle]');
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      try { this.userStill = sessionStorage.getItem('kekilli-atelier-still') === 'true'; } catch (_) { this.userStill = false; }
      this.schedule = () => { if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = null; this.renderMotion(); }); };
      const options = { signal: this.abort.signal };
      window.addEventListener('scroll', this.schedule, { ...options, passive: true });
      window.addEventListener('resize', () => { this.setMode(); this.schedule(); }, options);
      this.reduce.addEventListener('change', () => this.setMode(), options);
      this.mobile.addEventListener('change', () => this.setMode(), options);
      if (this.button) {
        this.button.hidden = false;
        this.button.addEventListener('click', () => {
          this.userStill = !this.still;
          try { sessionStorage.setItem('kekilli-atelier-still', String(this.userStill)); } catch (_) {}
          this.setMode();
        }, options);
      }
      if (this.video) {
        this.video.addEventListener('loadedmetadata', () => this.schedule(), options);
        this.video.addEventListener('loadeddata', () => { this.film.classList.add('is-video-ready'); this.updateFilmStatus(); this.schedule(); }, options);
        this.video.addEventListener('seeked', () => { this.film.dataset.time = this.video.currentTime.toFixed(3); this.seekFilm(); }, options);
        this.video.addEventListener('error', () => { this.videoError = true; this.film.classList.remove('is-scrubbable'); this.updateFilmStatus(); }, options);
        this.filmButton.hidden = false;
        this.filmButton.addEventListener('click', () => {
          if (this.still) {
            this.manualFilm = true; this.video.controls = true; this.loadFilm(true); this.video.play().catch(() => {}); this.updateFilmStatus();
          } else {
            if (navigator.connection?.saveData && !this.dataApproved) this.dataApproved = true;
            else this.filmPaused = !this.filmPaused;
            this.setMode();
          }
        }, options);
        navigator.connection?.addEventListener?.('change', () => this.setMode(), options);
      }
      this.setMode();
      this.refreshDaily();
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refreshDaily(); }, options);
      this.renderReviews();
      window.addEventListener('load', () => this.renderReviews(), options);
      if (this.film && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.filmNear = true; this.loadFilm(); }
        }, { rootMargin: '700px' });
        this.observer.observe(this.film);
      } else if (this.film) {
        this.filmNear = true;
        this.loadFilm();
      }
      this.schedule();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      clearTimeout(this.dailyTimer);
      this.video?.pause();
    }
    setMode() {
      this.still = this.reduce.matches || this.userStill;
      this.classList.toggle('has-motion', !this.still);
      const filmEnabled = !this.still && !this.filmPaused && (!navigator.connection?.saveData || this.dataApproved === true) && !this.videoError;
      this.film?.classList.toggle('is-scrubbable', filmEnabled);
      if (!this.manualFilm || !this.still) this.video?.pause();
      if (!this.still && this.video) { this.manualFilm = false; this.video.controls = false; }
      this.updateFilmStatus();
      if (this.button) {
        this.button.setAttribute('aria-pressed', String(this.still));
        this.button.querySelector('[data-motion-label]').textContent = this.reduce.matches ? this.button.dataset.reduced : this.still ? this.button.dataset.off : this.button.dataset.on;
        this.button.disabled = this.reduce.matches;
        this.button.title = this.reduce.matches ? this.button.dataset.off : '';
      }
      if (this.filmNear) this.loadFilm();
      this.schedule();
    }
    updateFilmStatus() {
      if (!this.filmButton) return;
      const l = this.filmLabels;
      this.filmButton.textContent = this.still ? l.manual : this.film?.classList.contains('is-scrubbable') ? l.pause : l.enable;
      this.filmButton.setAttribute('aria-pressed', String(!!this.filmPaused));
      this.filmStatus.textContent = this.videoError ? l.error : this.reduce.matches ? l.reduced : this.still || this.filmPaused ? l.still : navigator.connection?.saveData && !this.dataApproved ? l.saving : this.video?.readyState >= 2 ? l.scroll : l.loading;
    }
    loadFilm(explicit = false) {
      if (!this.video || this.video.src || (!explicit && !this.film?.classList.contains('is-scrubbable'))) return;
      this.video.src = this.mobile.matches ? this.video.dataset.mobileSrc : this.video.dataset.desktopSrc;
      this.video.preload = 'auto';
      this.video.load();
    }
    seekFilm() {
      if (!this.video || !this.film.classList.contains('is-scrubbable') || this.video.seeking || !Number.isFinite(this.video.duration)) return;
      const target = (this.filmProgress || 0) * Math.max(0, this.video.duration - .08);
      if (Math.abs(this.video.currentTime - target) > .025) this.video.currentTime = target;
    }
    static sydneyDate(now = new Date()) {
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
      const value = type => parts.find(part => part.type === type).value;
      return value('year') + '-' + value('month') + '-' + value('day');
    }
    static selectedProducts(products) {
      const ids = ["15409796284459","10281161162795","10276140580907","15399490191403","10275101507627","10270268129323","8117090091051","8117105295403","15402283827243","15397926961195","10275155050539","10270185226283","8091946876971","8091939176491"];
      const byId = new Map(products.map(product => [String(product.id), product]));
      return ids.map(id => byId.get(id)).filter(Boolean);
    }
    static dailyProducts(products, date) {
      const catalog = this.selectedProducts(products);
      if (!catalog.length) return [];
      const day = Math.floor(Date.parse(date + 'T00:00:00Z') / 86400000);
      const start = ((day * 3) % catalog.length + catalog.length) % catalog.length;
      return Array.from({ length: Math.min(3, catalog.length) }, (_, index) => catalog[(start + index) % catalog.length]);
    }
    static nextMidnight(now = Date.now()) {
      const date = this.sydneyDate(new Date(now));
      let low = now, high = now + 27 * 3600000;
      while (high - low > 1) { const mid = Math.floor((low + high) / 2); if (this.sydneyDate(new Date(mid)) === date) low = mid; else high = mid; }
      return high;
    }
    async readCatalog() {
      const grid = this.querySelector('[data-daily-rings]');
      if (!grid) return [];
      let url = new URL(grid.dataset.feed, location.origin), total = null;
      const products = [], seen = new Set();
      while (url) {
        if (url.origin !== location.origin) throw Error('Unexpected catalogue origin');
        url.searchParams.set('view', 'atelier-feed');
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        if (seen.has(url.href)) throw Error('Repeated catalogue page');
        seen.add(url.href);
        const response = await fetch(url, { signal: this.abort.signal, credentials: 'same-origin' });
        if (!response.ok) throw Error('Catalogue unavailable');
        const page = await response.json();
        if (!Array.isArray(page.products) || !Number.isInteger(page.total)) throw Error('Invalid catalogue');
        if (total !== null && total !== page.total) throw Error('Catalogue changed during loading');
        total = page.total; products.push(...page.products);
        url = page.next ? new URL(page.next, url) : null;
      }
      if (new Set(products.map(product => product.id)).size !== total) throw Error('Incomplete catalogue');
      return KekilliAtelier.selectedProducts(products);
    }
    async refreshDaily() {
      if (this.catalogLoading) return;
      clearTimeout(this.dailyTimer);
      const date = KekilliAtelier.sydneyDate();
      try {
        if (date !== this.catalogDate && !this.catalogLoading) {
          this.catalogLoading = true;
          this.catalog = await this.readCatalog();
          this.catalogDate = date;
        }
        const displayDate = KekilliAtelier.sydneyDate();
        if (this.catalog && this.displayDate !== displayDate) this.renderDaily(displayDate);
      } catch (_) { /* Keep the valid Liquid-rendered selection if the feed is unavailable. */ }
      finally {
        this.catalogLoading = false;
        if (this.isConnected) this.dailyTimer = setTimeout(() => this.refreshDaily(), Math.max(100, KekilliAtelier.nextMidnight() - Date.now() + 100));
      }
    }
    renderDaily(date) {
      const grid = this.querySelector('[data-daily-rings]');
      if (!grid) return;
      const selected = KekilliAtelier.dailyProducts(this.catalog, date);
      const cards = selected.map((product, index) => {
        const card = document.createElement('article'); card.className = 'atelier-product-card'; card.dataset.productId = product.id;
        const link = document.createElement('a'); link.className = 'atelier-card-link'; link.href = product.url;
        const media = document.createElement('div'); media.className = 'atelier-card-media';
        const number = document.createElement('span'); number.className = 'atelier-card-number'; number.setAttribute('aria-hidden', 'true'); number.textContent = '0' + (index + 1); media.append(number);
        [product.image, product.alternate].forEach((src, i) => { if (!src) return; const image = document.createElement('img'); image.src = src; image.className = i ? 'atelier-card-worn' : 'atelier-card-front'; image.alt = i ? '' : product.title; image.loading = 'lazy'; image.width = 900; image.height = 1125; if (i) image.setAttribute('aria-hidden', 'true'); media.append(image); });
        const discover = document.createElement('span'); discover.className = 'atelier-card-view'; discover.setAttribute('aria-hidden', 'true'); discover.textContent = grid.dataset.discover + ' ↗'; media.append(discover);
        const info = document.createElement('div'); info.className = 'atelier-card-info';
        const label = document.createElement('div'), title = document.createElement('h3'), caption = document.createElement('p'); title.textContent = product.title; caption.textContent = grid.dataset.caption; label.append(title, caption);
        const price = document.createElement('span'); price.className = 'atelier-card-price'; price.textContent = (product.varies ? grid.dataset.from + ' ' : '') + product.price;
        info.append(label, price); link.append(media, info); card.append(link); return card;
      });
      grid.replaceChildren(...cards); grid.dataset.date = date; grid.dataset.catalogCount = this.catalog.length; this.displayDate = date;
    }
    renderReviews() {
      const grid = this.querySelector('[data-reviews]');
      if (!grid || !Array.isArray(window.trggGoogleReviews)) return;
      const reviews = window.trggGoogleReviews.filter(review => review.status && review.rating >= 4 && review.content && review.author_url?.startsWith('https://www.google.com/maps/'));
      grid.replaceChildren(...reviews.map(review => {
        const card = document.createElement('article'); card.className = 'atelier-review-card'; card.dataset.reviewId = review.id;
        const stars = document.createElement('p'); stars.className = 'atelier-review-stars'; stars.setAttribute('aria-label', review.rating + ' out of 5 stars'); stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
        const quote = document.createElement('blockquote'); quote.textContent = review.content; quote.id = 'atelier-review-' + this.dataset.sectionId + '-' + review.id;
        const button = document.createElement('button'); button.type = 'button'; button.textContent = grid.dataset.more; button.setAttribute('aria-expanded', 'false'); button.setAttribute('aria-controls', quote.id); button.addEventListener('click', () => { const expanded = button.getAttribute('aria-expanded') !== 'true'; button.setAttribute('aria-expanded', String(expanded)); quote.classList.toggle('is-expanded', expanded); button.textContent = expanded ? grid.dataset.less : grid.dataset.more; }, { signal: this.abort.signal });
        const author = document.createElement('a'); author.href = review.author_url; author.target = '_blank'; author.rel = 'noopener'; author.textContent = review.author_name;
        const source = document.createElement('p'); source.className = 'atelier-review-source'; source.textContent = grid.dataset.source;
        card.append(stars, quote, button, author, source); return card;
      }));
      grid.dataset.reviewCount = reviews.length;
    }
    renderMotion() {
      if (!this.hero || !this.stage) return;
      const clamp = value => Math.max(0, Math.min(1, value));
      const ease = (a, b, value) => { const t = clamp((value - a) / (b - a)); return t * t * (3 - 2 * t); };
      const heroRect = this.hero.getBoundingClientRect();
      this.classList.toggle('is-hero-visible', heroRect.bottom > 0 && heroRect.top < innerHeight);
      const travel = this.hero.offsetHeight - this.stage.offsetHeight;
      const p = this.still ? 0 : clamp(-heroRect.top / Math.max(1, travel));
      const early = ease(.14, .34, p), late = ease(.63, .82, p);
      const shift = ease(.18, .43, p) - ease(.61, .84, p);
      const desktop = !this.mobile.matches;
      const vars = {
        '--progress': p,
        '--copy-one': 1 - ease(.09, .25, p),
        '--copy-one-x': `${desktop ? -110 * early : 0}px`,
        '--copy-one-y': `${-20 * early}px`,
        '--copy-two': ease(.26, .4, p) * (1 - ease(.59, .72, p)),
        '--copy-two-x': `${desktop ? 40 * (1 - ease(.26, .4, p)) : 0}px`,
        '--copy-three': ease(.72, .85, p),
        '--copy-three-y': `${25 * (1 - ease(.72, .85, p))}px`,
        '--photo-one': 1 - early,
        '--photo-two': early * (1 - late),
        '--photo-three': late,
        '--ring-x': `${desktop ? innerWidth * (.17 * late - .24 * shift) : 0}px`,
        '--ring-y': `${desktop ? -28 * shift - 14 * late : 10 * shift + 90 * early - 30 * late}px`,
        '--ring-angle': `${-7 + 12 * early - 7 * late}deg`,
        '--ring-scale': 1 - .14 * early - .02 * late,
        '--seal-opacity': 1 - ease(.08, .25, p),
        '--seal-angle': `${10 + 45 * early}deg`,
        '--edition-opacity': 1 - ease(.05, .2, p)
      };
      for (const [key, value] of Object.entries(vars)) this.stage.style.setProperty(key, String(value));
      this.dataset.progress = p.toFixed(4);
      if (this.film?.classList.contains('is-scrubbable')) {
        const rect = this.film.getBoundingClientRect();
        const filmProgress = clamp(-rect.top / Math.max(1, this.film.offsetHeight - this.filmStage.offsetHeight));
        this.filmProgress = filmProgress;
        this.film.dataset.progress = filmProgress.toFixed(4);
        this.film.querySelector('.atelier-film-meter > span').style.transform = `scaleX(${filmProgress})`;
        this.seekFilm();
      }
      if (this.editorial) {
        const rect = this.editorial.getBoundingClientRect();
        const offset = this.still || this.mobile.matches ? 0 : (clamp((innerHeight - rect.top) / (innerHeight + rect.height)) - .5) * 35;
        this.editorial.style.setProperty('--editorial-y', `${offset}px`);
      }
    }
  }
  customElements.define('kekilli-atelier', KekilliAtelier);
})();

